import {
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconX,
  type TablerIcon,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";

import type { AppointmentStatus } from "@/types";

/**
 * Appointments rebuild 85-04 (D1): the ONE place the four appointment
 * outcomes get their order, colour, icon, tone and label. Every list, badge,
 * pill, chart and action menu reads from here — the audit found five colour
 * tables and nine action sets that had drifted apart.
 *
 * Palette follows the calendar, the most-used surface: scheduled blue,
 * completed green, cancelled grey (the chip fades), no-show red (the chip
 * gets the dashed red border).
 */
export const APPOINTMENT_STATUSES = [
  "scheduled",
  "completed",
  "cancelled",
  "no_show",
] as const satisfies readonly AppointmentStatus[];

export const TERMINAL_STATUSES: readonly AppointmentStatus[] = ["completed", "cancelled", "no_show"];

export function isTerminalStatus(status: AppointmentStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** The outcomes a row can move to from where it is: no ladder, one way back. */
export function statusTransitions(status: AppointmentStatus): AppointmentStatus[] {
  return isTerminalStatus(status) ? ["scheduled"] : ["completed", "no_show", "cancelled"];
}

export const STATUS_HEX: Record<AppointmentStatus, string> = {
  scheduled: "#3b82f6",
  completed: "#22c55e",
  cancelled: "#6b7280",
  no_show: "#ef4444",
};

export const STATUS_ICON: Record<AppointmentStatus, TablerIcon> = {
  scheduled: IconClock,
  completed: IconCheck,
  cancelled: IconX,
  no_show: IconAlertCircle,
};

/** shadcn Badge variant. */
export const STATUS_BADGE_VARIANT: Record<AppointmentStatus, "default" | "secondary" | "outline" | "destructive"> = {
  scheduled: "secondary",
  completed: "default",
  cancelled: "outline",
  no_show: "destructive",
};

/** Desktop pill tones (lead detail, lists). */
export type StatusTone = "accent" | "green" | "amber" | "red" | "neutral";
export const STATUS_TONE: Record<AppointmentStatus, StatusTone> = {
  scheduled: "accent",
  completed: "green",
  cancelled: "neutral",
  no_show: "red",
};

/** Mobile MBadge tones. */
export const STATUS_MBADGE_TONE: Record<AppointmentStatus, "amber" | "green" | "red" | "grey" | "blue"> = {
  scheduled: "blue",
  completed: "green",
  cancelled: "grey",
  no_show: "red",
};

/** Inbox pill tones: a live booking waits, everything else is inert. */
export const STATUS_INBOX_TONE: Record<AppointmentStatus, "ghost" | "waiting"> = {
  scheduled: "waiting",
  completed: "ghost",
  cancelled: "ghost",
  no_show: "ghost",
};

/**
 * Static-literal label resolver (T-72-01: never `t(statusVar)`). `t` must be
 * scoped to the "appointments" namespace.
 */
export function appointmentStatusLabel(status: AppointmentStatus, t: (key: string) => string): string {
  switch (status) {
    case "scheduled":
      return t("status.scheduled");
    case "completed":
      return t("status.completed");
    case "cancelled":
      return t("status.cancelled");
    case "no_show":
      return t("status.noShow");
    default:
      return status;
  }
}

/** Hook form of appointmentStatusLabel for components. */
export function useAppointmentStatusLabel(): (status: AppointmentStatus) => string {
  const t = useTranslations("appointments");
  return (status) => appointmentStatusLabel(status, t);
}
